import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, AlertCircle, IndianRupee, Calendar, User, Phone } from 'lucide-react';
import Layout from '@/components/Layout';

export default function PendingRentsPage() {
  const { pgId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPendingRents();
  }, [pgId]);

  const fetchPendingRents = async () => {
    try {
      const response = await api.getPendingRents(pgId);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to fetch pending rents');
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-600">Failed to load pending rents</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <Button
            onClick={() => navigate('/pgs')}
            variant="ghost"
            className="mb-4"
            data-testid="back-to-pgs"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to PG List
          </Button>

          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 font-heading">{data.pgName}</h1>
              <p className="text-slate-600 mt-1">Pending Rent Collection</p>
              <p className="text-sm text-slate-500 mt-1">
                Current Month: {getMonthName(data.currentMonth)}
              </p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl border-2 border-slate-200 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-700">Tenants with Dues</p>
                <p className="text-3xl font-bold text-slate-900">{data.pendingCount}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <IndianRupee className="w-10 h-10 opacity-80" />
              <div>
                <p className="text-sm opacity-90">Total Pending Amount</p>
                <p className="text-3xl font-bold">₹{data.totalPendingAmount.toLocaleString('en-IN')}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border-2 border-slate-200 p-6 shadow-sm">
            <Button
              onClick={() => navigate('/collect-rent')}
              className="w-full h-14 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold"
              data-testid="collect-rent-button"
            >
              <IndianRupee className="w-5 h-5 mr-2" />
              Collect Rent Now
            </Button>
          </div>
        </div>

        {/* Pending Rents List */}
        {data.pendingRents.length === 0 ? (
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-green-900 mb-2">All Caught Up! 🎉</h3>
            <p className="text-green-700">No pending rents. All tenants have paid for this month.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {data.pendingRents.map((tenant) => (
              <div
                key={tenant.tenantId}
                className="bg-white rounded-xl border-2 border-red-200 overflow-hidden shadow-sm"
                data-testid={`pending-tenant-${tenant.tenantId}`}
              >
                <div className="bg-red-50 border-b border-red-200 p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                          <User className="w-6 h-6 text-red-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-slate-900">{tenant.tenantName}</h3>
                          <div className="flex items-center gap-4 text-sm text-slate-600">
                            <span className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              {tenant.tenantPhone}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              Joined: {new Date(tenant.joinDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-slate-600 mb-1">Total Due</p>
                      <p className="text-2xl font-bold text-red-600">₹{tenant.totalDue.toLocaleString('en-IN')}</p>
                    </div>
                  </div>
                </div>

                <div className="p-4">
                  <p className="text-sm font-semibold text-slate-700 mb-3">Pending Months:</p>
                  <div className="space-y-2">
                    {tenant.dues.map((due, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                      >
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-500" />
                          <span className="font-medium text-slate-900">{getMonthName(due.month)}</span>
                          {index === 0 && tenant.dues.length > 1 && (
                            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">
                              Oldest
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-slate-900">₹{due.amount.toLocaleString('en-IN')}</span>
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full">
                            {due.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-900">
                      <span className="font-semibold">Note:</span> Monthly Rent: ₹{tenant.monthlyRent.toLocaleString('en-IN')}
                      {tenant.dues.length > 1 && ' • Multiple months pending'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Action Footer */}
        {data.pendingRents.length > 0 && (
          <div className="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-200 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">
                  ₹{data.totalPendingAmount.toLocaleString('en-IN')} Total Due
                </h3>
                <p className="text-sm text-slate-600">
                  Collect rent from {data.pendingCount} tenant{data.pendingCount > 1 ? 's' : ''} to clear all dues
                </p>
              </div>
              <Button
                onClick={() => navigate('/collect-rent')}
                className="h-14 px-8 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold shadow-lg"
              >
                <IndianRupee className="w-5 h-5 mr-2" />
                Collect Rent
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
